import { useEffect } from 'react';
import { getOptimizedUrl } from '../lib/cloudinary';

/**
 * SEOHead Component
 * 
 * Dynamically updates document head for SEO
 * - Title
 * - Meta description
 * - Open Graph tags
 * - Canonical URL
 */
const SEOHead = ({
  title,
  description,
  image,
  url,
  type = 'website',
  siteName = 'Feestcomité Leupegem',
}) => {
  useEffect(() => {
    // Update document title
    document.title = title ? `${title} | ${siteName}` : siteName;

    // Helper to update or create meta tag
    const setMetaTag = (property, content, isName = false) => {
      if (!content) return;
      
      const attr = isName ? 'name' : 'property';
      let element = document.querySelector(`meta[${attr}="${property}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attr, property);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Meta description
    setMetaTag('description', description, true);

    // Open Graph tags
    setMetaTag('og:title', title);
    setMetaTag('og:description', description);
    setMetaTag('og:type', type);
    setMetaTag('og:site_name', siteName);
    
    if (url) {
      setMetaTag('og:url', url);
    }
    
    if (image) {
      // Use optimized image for OG
      const ogImage = getOptimizedUrl(image, 'cover', 'desktop');
      setMetaTag('og:image', ogImage);
      setMetaTag('og:image:width', '1200');
      setMetaTag('og:image:height', '630');
    }

    // Twitter Card tags
    setMetaTag('twitter:card', 'summary_large_image', true);
    setMetaTag('twitter:title', title, true);
    setMetaTag('twitter:description', description, true);
    if (image) {
      setMetaTag('twitter:image', getOptimizedUrl(image, 'cover', 'desktop'), true);
    }

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (url) {
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', url);
    }

    // Cleanup function - reset to defaults when component unmounts
    return () => {
      document.title = siteName;
    };
  }, [title, description, image, url, type, siteName]);

  return null; // This component doesn't render anything
};

export default SEOHead;
